import { useReveal } from "@/lib/useReveal";
import { ArrowUpRight } from "lucide-react";

const products = [
  {
    name: "PIRAVI",
    badge: "Patient Platform",
    tagline: "Where every health journey finds clarity.",
    body: "An evidence-led patient experience connecting people to validated information, longitudinal records, and the right clinical pathways, quietly, securely, and without friction.",
    accent: "#D62828",
    code: "01 / P-01",
  },
  {
    name: "INSURE BLU",
    badge: "Insurance Intelligence",
    tagline: "Insurance, decoded for healthcare.",
    body: "A digital layer that simplifies cashless workflows, claims, and clinical-financial decisioning between patients, payers, and providers across India.",
    accent: "#0F1B2D",
    code: "02 / P-02",
  },
  {
    name: "ZIMLAB",
    badge: "Practitioner Suite",
    tagline: "Laboratory-grade tools for modern clinicians.",
    body: "A precision toolkit for laboratories and practitioners, built on validated workflows, structured data, and a compliance-first foundation.",
    accent: "#A4161A",
    code: "03 / P-03",
  },
];

export default function WhatWeBuild() {
  const ref = useReveal();
  return (
    <section
      id="build"
      ref={ref}
      data-testid="build-section"
      className="relative bg-[#F8F9FA] py-28 md:py-40 lg:py-48"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-[#5F6368] reveal">
          <span className="inline-block h-px w-10 bg-[#D62828]" />
          What We Build
        </div>

        <div className="mt-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <h2
            data-testid="build-headline"
            className="font-display text-[clamp(2.5rem,5.5vw,5.25rem)] leading-[0.95] tracking-[-0.035em] text-[#111111] max-w-3xl reveal"
          >
            A pipeline of products engineered for the{" "}
            <span className="font-serif-italic italic text-[#D62828]">
              long arc
            </span>{" "}
            of healthcare.
          </h2>
          <p className="max-w-sm text-[15px] leading-[1.65] text-[#5F6368] reveal">
            Three product platforms working in concert, each addressing a
            structural problem in modern healthcare delivery.
          </p>
        </div>

        {/* Product cards */}
        <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-12 gap-6">
          {products.map((p, i) => (
            <article
              key={p.name}
              data-testid={`product-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={`reveal group relative overflow-hidden rounded-3xl bg-white border border-[#CED4DA] p-8 md:p-10 lg:p-12 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(15,27,45,0.25)] transition-all duration-700 ${
                i === 0 ? "md:col-span-7" : i === 1 ? "md:col-span-5" : "md:col-span-12"
              }`}
            >
              {/* code + badge */}
              <div className="flex items-start justify-between">
                <span
                  className="inline-flex items-center gap-2 rounded-full border border-[#CED4DA] bg-[#F8F9FA] px-3 py-1 text-[10px] uppercase tracking-[0.24em]"
                  style={{ color: p.accent }}
                >
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ background: p.accent }}
                  />
                  {p.badge}
                </span>
                <span className="text-[10px] uppercase tracking-[0.24em] text-[#5F6368]">
                  {p.code}
                </span>
              </div>

              <div className="mt-10 md:mt-14 grid grid-cols-12 gap-6 lg:gap-10 items-end">
                <div className={`col-span-12 ${i === 1 ? "" : "lg:col-span-7"}`}>
                  <h3
                    className="font-display tracking-[-0.04em] leading-[0.9] text-[#111111] break-words"
                    style={{
                      fontSize:
                        i === 1
                          ? "clamp(2rem, 3.6vw, 3.5rem)"
                          : "clamp(2.4rem, 5.2vw, 5rem)",
                    }}
                  >
                    {p.name}
                  </h3>
                  <p className="mt-4 font-serif-italic italic text-[18px] md:text-[20px] text-[#111111]/85 leading-snug">
                    {p.tagline}
                  </p>
                </div>
                <p
                  className={`col-span-12 ${
                    i === 1 ? "" : "lg:col-span-5"
                  } text-[14px] md:text-[15px] leading-[1.7] text-[#5F6368]`}
                >
                  {p.body}
                </p>
              </div>

              <div className="mt-10 flex items-center justify-between border-t border-[#CED4DA] pt-5">
                <span className="text-[11px] uppercase tracking-[0.22em] text-[#5F6368]">
                  Status · In Development
                </span>
                <button
                  data-testid={`product-cta-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="inline-flex items-center gap-2 text-[13px] font-semibold text-[#111111] group-hover:text-[#D62828] transition-colors"
                >
                  Learn more
                  <ArrowUpRight
                    size={16}
                    className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </button>
              </div>

              {/* Decorative accent line */}
              <div
                className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full opacity-[0.08]"
                style={{ background: p.accent }}
              />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
